/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable dot-notation */
import * as React from 'react';
import { useIntl } from 'react-intl';
import { validate as uuidValidate } from 'uuid';
import dayjs from 'dayjs';
import {
  CmdAlert,
  CmdGroupChipSelect,
  CmdInput,
  CmdRadioButton,
  CmdSubpage,
  CmdSubpageButton,
  CmdSwitch,
  CmdTextarea,
} from '@commander-services/gui-components';
import CmdBox, { CmdBoxButton } from '@commander-services/cmd-box';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { AxiosResponse } from 'axios';
import { IValidationApiResponse } from '../../Services/FormikValidatorService';
import RuleForm from './RuleForm';
import TimeFrameForm from './TimeFrameForm';
import { IChipInputItem } from '../Forms/Fields/ChipInput';
import VehicleGroupsForm from './VehicleGroupForm';
import RecipientForm from './RecipientForm';
import VehicleListForm from './VehicleListForm';
import VehicleGroupListForm from './VehicleGroupListForm';
import EditRuleForm from './EditRuleForm';
import VehicleListInGroupForm from './VehicleListInGroupForm';
import ContentEditableTextarea2, { ITagifyItem } from '../Forms/Fields/ContentEditableTextarea2';
import showMessage from '../Toastr/ToastService';
import PermissionsService, { PERMISSION_ALARM } from '../../Services/PermissionsService';
import usePageWithForm from '../../hooks/usePageWithForm';
import OnlineHelp from '../OnlineHelp';
import {
  GroupSelectVehiclesGroupsOption,
  IAlarmApiResponse,
  IAlarmDataApiResponse,
  IAlarmValidationApiResponse,
  ICondition,
  IGroupDataVehicleGroupForm,
  IGroupSelectVehicles,
  IRecipient,
  IRuleDataToSent,
  IRuleFormValues,
  ISelectedChips,
  ISelectedEditChips,
  IVehiclesGroupsDataObject,
  RuleFormValue,
  TimeFrameDataType,
  TimeFrameFormDataType,
} from './types';
import ChipInput from '../Forms/ReactHookFormFields/ChipInput';
import GroupChipSelectUniqueHookForm from '../Forms/ReactHookFormFields/GroupChipSelectUniqueHookForm';
import Calendar from '../Forms/ReactHookFormFields/Calendar';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { rideOwnershipValueAtom, ridePrivacyValueAtom } from './AlarmState';
import {
  createChipsForConditions,
  createChipsForTimeFrames,
  getData,
  getDefaultRecipient,
  getValidation,
  prepareRecipients,
} from './AlarmService';
import useAnalytics from '../../hooks/useAnalytics';
import {
  postRequest,
  putRequest,
  RESPONSE_STATUS_ERROR,
  RESPONSE_STATUS_OK,
} from '../../Services/HttpService';

interface IFieldWarning {
  [fieldName: string]: string;
}

interface IAlarmFormProps {
  closeCallback: (value?: string) => void;
  updateTable: () => void;
  id?: number | null;
  customerId?: number;
}

interface IAssignToVehicleGroup {
  name: string;
  vehicleId: Array<string | number>;
}

type TEnable = 0 | 1;

interface IAlarmFormValues {
  enabled: TEnable;
  name: string;
  validFrom: Date;
  validTo: Date;
  description: string;
  minPending: number;
  rideOwnership: string;
  ridePrivacy: string;
  conditions: IChipInputItem[];
  timeFrames: IChipInputItem[];
  vehicleId: string[];
  vehicleGroupId: string[];
  notificationMessage: '';
  recipients: IRecipient[] | null;
}

interface IAlarmFormSubmitValues
  extends Omit<
    IAlarmFormValues,
    'enabled' | 'conditions' | 'validTo' | 'timeFrames' | 'notificationMessage'
  > {
  enabled: boolean;
  conditions: IRuleDataToSent[];
  assignToVehicleGroup?: IAssignToVehicleGroup[];
  notificationMessage: string;
  validTo: Date | string;
  timeFrames: TimeFrameFormDataType;
  vehicleList?: string[];
  vehicleGroupList?: string[];
}

export default function AlarmForm(props: IAlarmFormProps): JSX.Element {
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  usePageWithForm();
  const { formatMessage: f } = useIntl();
  const [editingData, setEditingData] = React.useState<IAlarmDataApiResponse | null>(null);
  const [isRuleModalOpen, setIsRuleModalOpen] = React.useState<boolean>(false);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = React.useState<boolean>(false);
  const [isTimeFrameModalOpen, setIsTimeFrameModalOpen] = React.useState<boolean>(false);
  const [isVehicleListOpen, setIsVehicleListOpen] = React.useState<boolean>(false);
  const [firstSettingsOfVehicles, setFirstSettingsOfVehicles] = React.useState<boolean>(true);
  const [isVehicleGroupListOpen, setIsVehicleGroupListOpen] = React.useState<boolean>(false);
  const [clickedVehicleGroupId, setClickedVehicleGroupId] = React.useState<null | string>(null);
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [recipientValues, setRecipientValues] = React.useState<IRecipient[] | null>(null);
  const [groupSelectVehicles, setGroupSelectVehicles] = React.useState<IGroupSelectVehicles>({});
  const [editingRecipient, setEditingRecipient] = React.useState<IRecipient | null>(null);
  const [groupSelectVehiclesGroups, setGroupSelectVehiclesGroups] =
    React.useState<GroupSelectVehiclesGroupsOption>({});
  const [isVehicleGroupsModal, setIsVehicleGroupsModal] = React.useState<boolean>(false);
  const [loader, setLoader] = React.useState<boolean>(false);
  const [isAlertShown, setIsAlertShown] = React.useState<boolean>(false);
  const [_isDeactivateWarningShown, _setIsDeactivateWarningShown] = React.useState<boolean>(false);
  const [isAlarmDeactivated, setIsAlarmDeactivated] = React.useState<boolean>(false);
  const [rideOwnershipValue, setRideOwnershipValue] =
    useRecoilState<string>(rideOwnershipValueAtom);
  const [ridePrivacyValue, setRidePrivacyValue] = useRecoilState<string>(ridePrivacyValueAtom);

  const [conditionsData, setConditionsData] = React.useState<IRuleDataToSent[]>([]);
  const [timeframeData, setTimeframeData] = React.useState<TimeFrameFormDataType>([]);
  const [editDataConditions, setEditDataConditions] = React.useState<IRuleDataToSent | null>(null);
  const [vehiclesForNewGroup, setVehiclesForNewGroup] = React.useState<(string | number)[]>([]);
  const [isEditRuleModalOpen, setiIsEditRuleModalOpen] = React.useState<boolean>(false);
  const [editTimeFrameId, setEditTimeFrameId] = React.useState<number | string | null>(null);
  const [isDisabledSwitch, _setDisabledSwitch] = React.useState<boolean>(false);
  const [newVehicleGroupsArray, setNewVehicleGroupsArray] = React.useState<
    IGroupDataVehicleGroupForm[]
  >([]);
  const [isEditConditions, setEditConditions] = React.useState<boolean>(false);
  const [isDisabledTooltip, setIsDisabledTooltip] = React.useState<boolean>(false);
  const [_responseValidation, setResponseValidation] =
    React.useState<IAlarmValidationApiResponse | null>(null);
  const [backendValidationErrors, setBackendValidationErrors] = React.useState<Record<
    string,
    string[]
  > | null>(null);
  const [conditionValues, setConditionValues] = React.useState<IChipInputItem[]>([]);
  // const [notifMessageValidation, setNotifMessageValidation] = React.useState<string[] | null>(null);
  const [notifMessageWarningValidation, setNotifMessageWarningValidation] = React.useState<
    string[]
  >([]);
  const [notificationMessageError, setNotificationMessageError] = React.useState<string | null>(
    null
  );
  const [isNotificationMessageRequired, setIsNotificationMessageRequired] =
    React.useState<boolean>(false);

  const [notifMessageValue, setNotifMessageValue] = React.useState<string>('');
  const [validToValue, _setValidToValue] = React.useState<string>('');
  const [minPending, _setMinPending] = React.useState<number>(0);
  const [fieldWarnings, setFieldWarnings] = React.useState<IFieldWarning>({});
  const [waypointIdDeleted, setWaypointIdDeleted] = React.useState<string[]>([]);
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<IAlarmFormValues>({
    mode: 'onChange',
    defaultValues: {
      enabled: 1,
      name: '',
      validFrom: undefined,
      validTo: undefined,
      description: '',
      minPending: 0,
      rideOwnership: 'own',
      ridePrivacy: 'public',
      conditions: conditionValues,
      timeFrames: [],
      vehicleId: [],
      vehicleGroupId: [],
      notificationMessage: '',
      recipients: recipientValues,
    },
    resolver: async (formData) => {
      if (!validation) {
        return { values: formData, errors: {} };
      }

      const formValidation = {
        ...validation,
        condition: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      };

      const validateErros: { [key: string]: string } = validateFields(formData, formValidation);

      const toDate = dayjs(formData.validTo);
      const parseDateToday24 = new Date(new Date().setHours(23, 59, 59, 999));
      const minDate = dayjs(parseDateToday24);
      const formattedMinDate = minDate.format('DD.MM.YYYY HH:mm');
      if (minDate.isAfter(toDate) && formData.validFrom && formData.validTo) {
        validateErros.validTo = `${f({ id: 'datepicker.minDate' })} ${formattedMinDate}`;
      }
      const fromDate = dayjs(formData.validFrom);
      if (fromDate.isAfter(toDate) && formData.validFrom && formData.validTo) {
        validateErros.validFrom = f({ id: 'datepicker.validityFromToValidation' });
      }
      if (validateErros.vehicleId && formData.vehicleGroupId.length === 0) {
        validateErros.vehicleId = f({ id: 'alarms.section.vehicles.error' });
      }

      const formErrors = transformErrors(validateErros);

      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const getErrorWarning = (values: IAlarmFormValues): boolean => {
    if (String(values.enabled) === '1') {
      let vehicleError = true;
      if (values.vehicleId) {
        Object.keys(groupSelectVehicles).forEach((title: string) => {
          groupSelectVehicles[title].forEach((item) => {
            if (values.vehicleId.includes(String(item.value)) && item.passed === true) {
              vehicleError = false;
            }
          });
        });
      }
      let vehicleGroupError = true;
      if (values.vehicleGroupId) {
        Object.keys(groupSelectVehiclesGroups).forEach((title: string) => {
          groupSelectVehiclesGroups[title].forEach((item) => {
            if (values.vehicleGroupId.includes(String(item.value)) && item.passed === true) {
              vehicleGroupError = false;
            }
          });
        });
      }
      if (vehicleError && vehicleGroupError) {
        // setIsDeactivateWarningShown(true);
        // setIsAlarmDeactivated(true);
        // return true;
      }
    }
    return false;
  };

  const handeNotifMessageValidation = React.useCallback(() => {
    let notificationMessageRequired = false;
    if (recipientValues && recipientValues.length > 0) {
      recipientValues.forEach((item) => {
        if (item.email && item.viaEmail) {
          notificationMessageRequired = true;
        }
      });
    }
    if (notificationMessageRequired) {
      setIsNotificationMessageRequired(true);
    } else {
      setIsNotificationMessageRequired(false);
    }
    return notificationMessageRequired;
  }, [recipientValues]);

  const handleOnSubmit = async (values: IAlarmFormValues): Promise<boolean> => {
    trackEvent(['alarm', 'click', 'Alarm form submit']);
    let notifMessageErr = true;
    if (isNotificationMessageRequired && notifMessageValue.trim().length === 0) {
      setNotificationMessageError(f({ id: 'form.required' }));
      return false;
    }
    if (notificationMessageError === f({ id: 'form.required' })) {
      setNotificationMessageError(null);
      notifMessageErr = false;
    }
    if (
      !getErrorWarning(values) &&
      (!notificationMessageError || !notifMessageErr) &&
      recipientValues &&
      recipientValues.length > 0
    ) {
      setLoader(true);
      const vehicleGroupIds: string[] = [];
      Object.values(JSON.parse(JSON.stringify(values.vehicleGroupId)) as string[]).forEach(
        (id: string) => {
          const tempValue = id.split('_');
          if (!tempValue || !tempValue[0] || tempValue[0] !== 'temp') {
            vehicleGroupIds.push(id);
          }
        }
      );

      let conditionIds: string[] = [];
      Object.values(values.conditions).forEach((item) => {
        conditionIds = conditionIds.concat(String(item.id));
      });
      let timeframeIds: string[] = [];
      Object.values(values.timeFrames).forEach((item) => {
        timeframeIds = timeframeIds.concat(String(item.id));
      });

      const newValues: IAlarmFormSubmitValues = {
        ...values,
        minPending: values.minPending ? Number(values.minPending) : 0,
        enabled: String(values.enabled) === '1',
        recipients: recipientValues ? prepareRecipients(recipientValues) : values.recipients,
        conditions: conditionsData && conditionsData.filter((i) => conditionIds.includes(i.type)),
        notificationMessage: notifMessageValue,
        timeFrames: timeframeData.map((group) => {
          const groupItems = group.filter(
            (i) => i.recordId !== null && timeframeIds.includes(i.recordId)
          );

          const newGroupItems = groupItems.map((item) => {
            const newItem = {
              ...item,
              recordId: uuidValidate(item.recordId as string) ? null : item.recordId,
            };
            return newItem;
          });
          return newGroupItems;
        }),
        vehicleGroupId: vehicleGroupIds,
        validTo: JSON.stringify(values.validTo) === '[]' ? '' : values.validTo,
      };

      // Valid to date to handle 23:59:59 time.
      if (newValues['validTo'] !== undefined) {
        newValues['validTo'] = dayjs(values.validTo)
          .toISOString()
          .replace('23:00:00.000Z', '22:59:59.999Z');
      }

      if (newVehicleGroupsArray) {
        // const assignToVehicleGroup: IAssignToVehicleGroup[] = newVehicleGroupsArray;
        const assignToVehicleGroup: IAssignToVehicleGroup[] = newVehicleGroupsArray.map(
          (group) => ({
            ...group,
            vehicleId: group.vehicleId.map(String),
          })
        );
        newValues['assignToVehicleGroup'] = assignToVehicleGroup;
      }

      const response: AxiosResponse | false = props.id
        ? await putRequest(`/v1/alarm/${props.id}/edit`, newValues, {
            showToaster: false,
            returnAxiosResponse: true,
          })
        : await postRequest('/v1/alarm/add', newValues, {
            showToaster: false,
            returnAxiosResponse: true,
          });
      setLoader(false);
      if (
        response &&
        (response as AxiosResponse).data &&
        (response as AxiosResponse).data.status === RESPONSE_STATUS_OK
      ) {
        const responseData = response.data;
        showMessage(
          '',
          responseData.toast.message,
          responseData.toast.type.toLowerCase(),
          responseData.toast.duration
        );
        props.updateTable();
        props.closeCallback('submit');
        return true;
      }
      if (
        response &&
        response.data &&
        response.data.validation &&
        !response.data.validation.passed &&
        response.data.status === RESPONSE_STATUS_ERROR
      ) {
        const responseWarning = response.data.validation.errors;

        let toastErrorsText = f({ id: `alarms.warning.toaster.message` });
        Object.keys(responseWarning).forEach((fieldName: string) => {
          const sectionLocaleString = f({ id: `alarms.toastr.section.name.${fieldName}` });
          // toastErrorsText += `<p>${f({ id: `alarms.toastr.section.name.${fieldName}` })}</p>`};
          toastErrorsText += `<p><strong>${sectionLocaleString}</strong></p>`;
          toastErrorsText += `<p>`;
          toastErrorsText += (responseWarning[fieldName] as string[]).join('<br />');
          toastErrorsText += `</p>`;
        });

        showMessage(f({ id: `alarms.warning.toaster.title` }), toastErrorsText, 'warning');
        return false;
      }
      if (
        response &&
        response.data &&
        response.data.validation &&
        response.data.validation.passed &&
        response.data.status === RESPONSE_STATUS_ERROR
      ) {
        showMessage(
          f({ id: `alarms.warning.toaster.title` }),
          (response as AxiosResponse).data.toast.message,
          'warning'
        );
        return false;
      }
    }
    return true;
  };

  const handleCloseRuleModal = React.useCallback(() => {
    setIsRuleModalOpen(!isRuleModalOpen);
    trackEvent(['alarm', 'click', 'Alarm form close rule modal']);
  }, [isRuleModalOpen]);

  const handleCloseEditRuleModal = React.useCallback(() => {
    setiIsEditRuleModalOpen(!isEditRuleModalOpen);
    trackEvent(['alarm', 'click', 'Alarm form close edit rule modal']);
  }, [isEditRuleModalOpen]);

  const handleRuleModal = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      const editId = (event.target as HTMLElement).id;
      trackEvent(['alarm', 'click', `Alarm form open rule modal ${editId}`]);

      if (editId === 'chip-conditions-false') {
        setIsDisabledTooltip(true);
        return;
      }
      setIsDisabledTooltip(false);

      setIsRuleModalOpen(!isRuleModalOpen);
    },
    [isRuleModalOpen]
  );

  const handleEditRuleModal = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      const editId = event.currentTarget.getAttribute('data-value');
      trackEvent(['alarm', 'click', `Alarm form open edit rule modal ${editId}`]);

      if (event.currentTarget.id === 'chip-conditions' && conditionsData) {
        const filteredConditionsData = conditionsData.filter((item) => item.type === editId)[0];

        setEditDataConditions(filteredConditionsData);

        // setValue('conditions', [...conditionsData, filteredConditionsData]);
      } else {
        setEditDataConditions(null);
      }

      setiIsEditRuleModalOpen(!isEditRuleModalOpen);
    },
    [conditionsData, isEditRuleModalOpen]
  );

  const handleRecipientModal = React.useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (event) {
        event.preventDefault();
      }
      setIsRecipientModalOpen(!isRecipientModalOpen);
      setEditingRecipient(null);
    },
    [isRecipientModalOpen]
  );

  const handleResetRuleModal = React.useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (event) {
        event.preventDefault();
      }
      setIsRuleModalOpen(false);
      setIsRuleModalOpen(true);
    },
    []
  );

  const handleResetTimeFrameModal = React.useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (event) {
        event.preventDefault();
      }
      setIsTimeFrameModalOpen(false);
      setIsTimeFrameModalOpen(true);
    },
    []
  );

  const handleTimeFrameModal = React.useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      trackEvent(['alarm', 'click', 'Alarm form open time frame modal']);
      if (event) {
        event.preventDefault();
        setEditTimeFrameId(null);
        setIsTimeFrameModalOpen(!isTimeFrameModalOpen);
      } else {
        setEditTimeFrameId(null);
        setIsTimeFrameModalOpen(!isTimeFrameModalOpen);
      }
    },
    [isTimeFrameModalOpen]
  );

  const handleEditTimeFrameModal = React.useCallback(
    (event?: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      trackEvent(['alarm', 'click', 'Alarm form open edit time frame modal']);
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        const timeFrameGroupId = event.currentTarget.getAttribute('data-value');
        if (timeFrameGroupId) {
          setEditTimeFrameId(timeFrameGroupId);
        }
      }
      setIsTimeFrameModalOpen(!isTimeFrameModalOpen);
    },
    [isTimeFrameModalOpen]
  );

  const handleVehicleGroupsModal = React.useCallback(
    (
      event?:
        | React.MouseEvent<HTMLAnchorElement, MouseEvent>
        | React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (event) {
        event.preventDefault();
      }
      trackEvent(['alarm', 'click', 'Alarm form open vehicle groups modal']);

      setIsAlertShown(false);
      setIsVehicleGroupsModal(!isVehicleGroupsModal);
    },
    [isVehicleGroupsModal]
  );

  /** Close alert */
  const handleCloseAlert = React.useCallback(() => {
    setIsAlertShown(false);
  }, []);

  // const handleCloseDeactivateWarning = React.useCallback(() => {
  //   setIsDeactivateWarningShown(false);
  // }, []);

  const ruleSectionButtons: CmdBoxButton[] = [
    {
      title: f({ id: 'alarms.section.conditions.addrule' }),
      tooltip: f({ id: 'alarms.section.conditions.addrule.tooltip' }),
      buttonCallback: handleRuleModal,
      id: 'open-rule-modal',
      className: '',
      type: 'button',
    },
  ];

  const getConditions = React.useCallback(
    (data: IChipInputItem[]) => {
      const getConditionsAsync = async () => {
        let tempData: IChipInputItem[] = [];

        tempData = Object.assign(watch('conditions'));
        data.forEach((values: IChipInputItem) => {
          tempData.forEach((formValues) => {
            if (values.type === formValues.id) {
              formValues.passed = values.passed;
            }
          });
        });

        setConditionValues(tempData);
      };
      getConditionsAsync();
    },
    [editingData, isEditConditions, rideOwnershipValue, ridePrivacyValue, conditionValues]
  );
  const getBusinessValidation = async () => {
    const vehicleGroupIds: string[] = [];
    Object.values(JSON.parse(JSON.stringify(watch('vehicleGroupId'))) as string[]).forEach(
      (id: string) => {
        const tempValue = id.split('_');
        if (!tempValue || !tempValue[0] || tempValue[0] !== 'temp') {
          vehicleGroupIds.push(id);
        }
      }
    );
    let conditionIds: string[] = [];
    Object.values(watch('conditions')).forEach((item: IChipInputItem) => {
      conditionIds = conditionIds.concat(String(item.id));
    });
    let timeframeIds: string[] = [];
    Object.values(watch('timeFrames')).forEach((item: IChipInputItem) => {
      timeframeIds = timeframeIds.concat(String(item.id));
    });

    const newValues: IAlarmFormSubmitValues = {
      ...getValues(),
      minPending: watch('minPending') ? Number(watch('minPending')) : 0,
      enabled: String(watch('enabled')) === '1',
      recipients: recipientValues ? prepareRecipients(recipientValues) : watch('recipients'),
      // conditions: conditionsData.filter((i: any) => conditionIds.includes(i.type)),
      conditions: conditionsData,
      notificationMessage: notifMessageValue,
      timeFrames: timeframeData.map((group) => {
        const groupItems = group.filter(
          (i) => i.recordId !== null && timeframeIds.includes(i.recordId)
        );

        const newGroupItems = groupItems.map((item) => {
          const newItem = {
            ...item,
            recordId: uuidValidate(item.recordId as string) ? null : item.recordId,
          };
          return newItem;
        });
        return newGroupItems;
      }),
      vehicleList: watch('vehicleId'),
      vehicleGroupList: vehicleGroupIds,
      validTo: JSON.stringify(watch('validTo')) === '[]' ? '' : watch('validTo'),
    };

    if (newVehicleGroupsArray && watch('vehicleGroupId').length > 0) {
      const assignToVehicleGroup: IAssignToVehicleGroup[] = newVehicleGroupsArray;
      newValues['assignToVehicleGroup'] = assignToVehicleGroup;
    } else {
      newValues['assignToVehicleGroup'] = [];
      setNewVehicleGroupsArray([]);
    }

    setLoader(true);
    const response: AxiosResponse | false = await postRequest(
      '/v1/alarm/form/business-validation',
      newValues,
      { returnAxiosResponse: true }
    );

    if (response && response.data && response.status === 200) {
      setBackendValidationErrors(response.data.validation.errors);

      setNotifMessageWarningValidation(
        response.data.alarm.notificationMessageErrorList.map((item: ITagifyItem) => item.value)
      );

      const tempCondition: IChipInputItem[] = response.data.alarm.conditions;

      getConditions(tempCondition);
      setLoader(false);
    } else {
      setLoader(false);
    }
  };

  /** Show alert about possibillity of creating a vehicle group */
  const showVehicleInfoAlert = React.useCallback((number: number, values: (string | number)[]) => {
    setValue('vehicleId', values as string[]);
    trigger('vehicleId');
    trigger('vehicleGroupId');
    getBusinessValidation();

    if (number > 9) {
      setIsAlertShown(true);
      setVehiclesForNewGroup(values);
      trigger('vehicleId');
      trigger('vehicleGroupId');
    }
  }, []);

  const handleShowVehicleList = React.useCallback(() => {
    setIsVehicleListOpen(!isVehicleListOpen);
    trackEvent(['alarm', 'click', 'Alarm form open vehicle list modal']);
  }, [isVehicleListOpen]);

  const handleShowVehicleGroupList = React.useCallback(() => {
    setIsVehicleGroupListOpen(!isVehicleGroupListOpen);
    trigger('vehicleGroupId');
    trigger('vehicleId');
    trackEvent(['alarm', 'click', 'Alarm form open vehicle group list modal']);
  }, [isVehicleGroupListOpen]);

  const handleShowVehicleListInGroup = React.useCallback((id: string) => {
    setClickedVehicleGroupId(String(id));
  }, []);

  const handleCloseClickedVehicleGroup = React.useCallback(() => {
    setClickedVehicleGroupId(null);
  }, []);

  const timeFrameButtons: CmdBoxButton[] = [
    {
      title: f({ id: 'alarms.section.timeFrame.addTimeFrame' }),
      tooltip: f({ id: 'alarms.section.timeFrame.addTimeFrame.tooltip' }),
      buttonCallback: handleTimeFrameModal,
      id: 'open-timeframe-modal',
      className: '',
      type: 'button',
    },
  ];

  const recipientSectionButtons: CmdBoxButton[] = [
    {
      title: f({ id: 'alarms.section.recipient.button.addRecipient' }),
      tooltip: f({ id: 'alarms.section.recipient.button.addRecipient.tooltip' }),
      buttonCallback: handleRecipientModal,
      id: 'open-recipient-modal',
      className: '',
      type: 'button',
    },
  ];

  /** Handle response from new recipient modal */
  const handleRecipientModalResponse = React.useCallback(
    (values: IRecipient) => {
      let recipient: IRecipient[] = [];
      if (recipientValues) {
        recipient = JSON.parse(JSON.stringify(recipientValues));
      }
      recipient.push(values);
      setRecipientValues(recipient);
      setValue('recipients', recipient);
    },
    [recipientValues]
  );

  /** Handle response from recipient editing modal */
  const handleRecipientModalEdit = React.useCallback(
    (values: IRecipient & { key: number }) => {
      let recipient: IRecipient[] = [];
      if (recipientValues) {
        recipient = JSON.parse(JSON.stringify(recipientValues));
      }
      recipient[values.key] = values;
      setRecipientValues(recipient);
      setEditingRecipient(null);
    },
    [recipientValues]
  );

  /** Get alarm data for editing and set validation errors from backend */
  const getAlarmData = async (): Promise<void> => {
    if (props.id) {
      setLoader(true);
      const responseAlarm: IAlarmApiResponse | null = await getData(props.id);
      const respValidation: IValidationApiResponse | null = (await getValidation()) || null;
      if (respValidation) {
        setValidation(respValidation);
      }
      if (responseAlarm) {
        const { validation: validationData } = responseAlarm;
        setEditingData(responseAlarm.alarm);
        setResponseValidation(validationData || null);
      }
      setLoader(false);
    }
  };

  /** Get validations for alarm form and set them to the state */
  const getAlarmValidation = async (): Promise<void> => {
    setLoader(true);
    const response: IValidationApiResponse | null = (await getValidation()) || null;
    if (response) {
      setValidation(response);
      setLoader(false);
    }
  };

  /** Get default recipients and set it to the state */
  const getRecipientValues = async (): Promise<void> => {
    setLoader(true);
    const response = await getDefaultRecipient();
    if (response) {
      setRecipientValues([response as IRecipient]);
      setValue('recipients', [response as IRecipient]);

      setLoader(false);
    }
  };

  const handleNotifMessageValue = (value: string) => {
    setNotifMessageValue(value);
    trackEvent(['alarm', 'change', `Alarm form change notification message: ${value}`]);
  };

  React.useEffect(() => {
    const isRquired = handeNotifMessageValidation();
    if (notifMessageValue.trim().length === 0 && !isRquired) {
      setNotificationMessageError(null);
    }
  }, [handeNotifMessageValidation, notifMessageValue]);

  const getGroupSelectVehicles = React.useCallback(() => {
    const getGroupSelectVehiclesAsync = async () => {
      // if (formikProps.current) {
      const conditionsDataForVehicle: string[] = [];

      conditionValues.forEach((element: IChipInputItem) => {
        conditionsDataForVehicle.push(String(element.id));
      });

      const data = {
        rideOwnership: watch('rideOwnership'),
        ridePrivacy: watch('ridePrivacy'),
        conditions: conditionsDataForVehicle,
        vehicleId: watch('vehicleId'),
      };

      const res: AxiosResponse | false = await postRequest('/v1/alarm/form/vehicleId', data, {
        returnAxiosResponse: true,
      });
      if (res && res.data && res.status === 200) {
        setGroupSelectVehicles(res.data);
        const responseData: IGroupSelectVehicles = res.data;

        if (!props.id && firstSettingsOfVehicles) {
          let vehicleIdLength = 0;
          let uniqueValue = '';
          Object.values(responseData).forEach((i) => {
            vehicleIdLength += i.length;
            if (i.length === 1 && i[0].value) {
              uniqueValue = String(i[0].value);
            }
          });
          if (vehicleIdLength === 1 && uniqueValue) {
            const vehicleIdValue = [uniqueValue];
            if (vehicleIdValue) {
              setValue('vehicleId', vehicleIdValue);
              trigger('vehicleId');
              if (isAlertShown) {
                setVehiclesForNewGroup(vehicleIdValue);
                trigger('vehicleId');
              }
              setFirstSettingsOfVehicles(false);
            }
          }
        }
      }
      // }
    };
    getGroupSelectVehiclesAsync();
  }, [
    conditionValues,
    firstSettingsOfVehicles,
    isAlertShown,
    props.id,
    groupSelectVehicles,
    watch('rideOwnership'),
    watch('ridePrivacy'),
  ]);

  const getGroupSelectVehiclesGroups = React.useCallback(
    (newGroupName?: string) => {
      const selectNewGroup = (data: IVehiclesGroupsDataObject, groupName: string): void => {
        const prevVehicleGroupId = watch('vehicleGroupId') || [];
        Object.keys(data).forEach((title: string) => {
          data[title].forEach((item) => {
            if (item.item === groupName) {
              prevVehicleGroupId.push(item.value as string);
            }
          });
        });
        setValue('vehicleGroupId', prevVehicleGroupId);
        trigger('vehicleGroupId');
      };

      const getGroupSelectVehiclesGroupsAsync = async () => {
        const conditionsDataForVehicleGroup: string[] = [];

        conditionValues.forEach((element: IChipInputItem) => {
          conditionsDataForVehicleGroup.push(String(element.id));
        });
        const data = {
          rideOwnership: watch('rideOwnership'),
          ridePrivacy: watch('ridePrivacy'),
          conditions: conditionsDataForVehicleGroup,
          assignToVehicleGroup: newVehicleGroupsArray,
          vehicleGroupId: watch('vehicleGroupId'),
        };
        const res: AxiosResponse | false = await postRequest(
          '/v1/alarm/form/vehicleGroupId',
          data,
          { returnAxiosResponse: true }
        );
        if (res && res.data && res.status === 200) {
          setGroupSelectVehiclesGroups(res.data);
          if (newGroupName) {
            selectNewGroup(res.data, newGroupName);
          }
        }
      };
      getGroupSelectVehiclesGroupsAsync();
    },
    [
      conditionValues,
      newVehicleGroupsArray,
      groupSelectVehiclesGroups,
      watch('rideOwnership'),
      watch('ridePrivacy'),
    ]
  );

  React.useEffect(() => {
    if (watch('validTo') && watch('validTo').toString().includes('00:00:00')) {
      const convertedValidTo = new Date(watch('validTo').setHours(23, 59, 59, 999));

      setValue('validTo', convertedValidTo);
    }
  }, [validToValue]);

  const handleVehicleGroupsData = React.useCallback(
    (name: string) => {
      newVehicleGroupsArray.push({
        name,
        vehicleId: vehiclesForNewGroup as number[],
        id: `temp_${newVehicleGroupsArray.length}`,
      });
      setNewVehicleGroupsArray(newVehicleGroupsArray);
      getGroupSelectVehiclesGroups(name);
      setValue('vehicleId', []);
      trigger('vehicleId');
      if (isAlertShown) {
        setVehiclesForNewGroup([]);
      }
    },
    [getGroupSelectVehiclesGroups, isAlertShown, newVehicleGroupsArray, vehiclesForNewGroup]
  );

  React.useEffect(() => {
    if (isAlarmDeactivated) {
      setValue('enabled', 0);
      setIsAlarmDeactivated(false);
    }

    if (editingData) {
      const newValues = {
        ...getValues(),
      };
      if (editingData.recipients) {
        setRecipientValues(editingData.recipients);
        setValue('recipients', editingData.recipients);
      }
      if (editingData.name) {
        newValues.name = editingData.name;
        setValue('name', editingData.name);
      }
      if (editingData.description) {
        newValues.description = editingData.description;
        setValue('description', editingData.description);
      }
      if (editingData.validFrom) {
        newValues.validFrom = new Date(editingData.validFrom);
        setValue('validFrom', new Date(editingData.validFrom));
      }
      if (editingData.validTo) {
        if (editingData.validTo.toString().includes('T')) {
          const convertedValidTo = new Date(
            new Date(editingData.validTo).setHours(23, 59, 59, 999)
          );
          setValue('validTo', convertedValidTo);
          newValues.validTo = convertedValidTo;
        }
      }

      // if (editingData.notificationMessage) {
      //   console.log('editingData.notificationMessage - value: ', editingData.notificationMessage);
      //   setNotifMessageValue(editingData.notificationMessage);
      // }
      // console.log('newValues: ', newValues);
      // formikProps.current.setValues(newValues);
    }
  }, [
    isAlarmDeactivated,
    // rideOwnershipValue,
    // conditionValues,
    // ridePrivacyValue,
    editingData,
    // getGroupSelectVehicles,
    // getGroupSelectVehiclesGroups,
    // formikProps,
  ]);

  React.useEffect(() => {
    if (editingData && editingData.notificationMessage) {
      if (editingData.notificationMessage) {
        setNotifMessageValue(editingData.notificationMessage);
      }
    }

    if (editingData && editingData.conditions) {
      setEditConditions(true);
      const tempDataHookForm: {
        id: string;
        label: string;
        passed: boolean;
        waypointDeleted?: boolean;
      }[] = [];
      const tempData: IRuleDataToSent[] = [];
      const deletedWaypointID: string[] = [];

      editingData.conditions.forEach((item: ICondition) => {
        tempData.push({
          type: item.type,
          value: Array.isArray(item.value)
            ? item.value.map(String)
            : typeof item.value === 'object' && item.value !== null
              ? { min: String(item.value.min), max: String(item.value.max) }
              : String(item.value),
        });
        if (item.waypointDeleted) {
          deletedWaypointID.push(item.type);
          tempDataHookForm.push({
            id: item.type,
            label: item.label,
            passed: item.passed,
            waypointDeleted: item.waypointDeleted,
          });
        } else {
          tempDataHookForm.push({ id: item.type, label: item.label, passed: item.passed });
        }
      });

      setWaypointIdDeleted(deletedWaypointID);

      setConditionValues(tempDataHookForm);

      setConditionsData(tempData);
      setValue('conditions', tempDataHookForm);
    }

    if (editingData && editingData.rideOwnership) {
      setValue('rideOwnership', editingData.rideOwnership);
    }
    if (editingData && editingData.ridePrivacy) {
      setValue('ridePrivacy', editingData.ridePrivacy);
    }

    if (editingData && editingData.minPending) {
      setValue('minPending', editingData.minPending);
    }
  }, [editingData]);

  React.useEffect(() => {
    if (editingData) {
      setLoader(true);
      const enabled = editingData.enabled === false ? 0 : 1;
      if (watch('enabled') !== enabled) {
        setValue('enabled', enabled);
      }
      setLoader(false);
    }
  }, [editingData?.enabled]);

  React.useEffect(() => {
    if (editingData && editingData.vehicleId) {
      const dataForVehicle = editingData.vehicleId.map((item: number) => {
        return String(item);
      });

      setValue('vehicleId', dataForVehicle);
      trigger('vehicleId');
    }
    if (editingData && editingData.vehicleGroupId) {
      const dataForVehicle: string[] = [];
      editingData.vehicleGroupId.forEach((item: number) => {
        dataForVehicle.push(String(item));
      });
      setValue('vehicleGroupId', dataForVehicle);
      trigger('vehicleGroupId');
    }
  }, [editingData]);

  // Set timeframe data from editing values
  React.useEffect(() => {
    if (editingData && editingData.timeFrames) {
      setTimeframeData(editingData.timeFrames);
    }
  }, [editingData]);

  React.useEffect(() => {
    setValue('timeFrames', createChipsForTimeFrames(timeframeData));
    setValue('conditions', createChipsForConditions(conditionValues));
  }, [timeframeData, conditionValues]);

  const handleRemoveTimeFrameItem = React.useCallback(
    (items: IChipInputItem[]) => {
      if (items.length === 0) {
        setTimeframeData([]);
      }
      trackEvent(['alarm', 'click', 'Alarm form remove time frame item']);

      const idsToKeep = items.map((item: IChipInputItem) => item.id);

      const newTimeFrameGroups: TimeFrameFormDataType = [];
      timeframeData.forEach((timeFrameGroup: TimeFrameDataType[]) => {
        const newTimeFrameGroupItems: TimeFrameDataType[] = [];
        timeFrameGroup
          .filter((item: TimeFrameDataType) => idsToKeep.includes(String(item.recordId)))
          .forEach((item: TimeFrameDataType) => {
            newTimeFrameGroupItems.push(item);
          });
        if (newTimeFrameGroupItems.length > 0) {
          newTimeFrameGroups.push(newTimeFrameGroupItems);
        }
      });

      setTimeframeData(newTimeFrameGroups);
    },
    [timeframeData]
  );

  const handleRemoveConditionItem = React.useCallback(
    (items: IChipInputItem[]) => {
      if (items.length === 0) {
        setConditionValues([]);
        setConditionsData([]);
        setValue('conditions', []);
      }
      trackEvent(['alarm', 'click', 'Alarm form remove condition item']);

      const waypointIdDeletedTemp: string[] = [];

      const idsToKeep = items.map((item: IChipInputItem) => item.id);

      const newConditionsValues = conditionValues.filter((item) => idsToKeep.includes(item.id));
      if (conditionsData) {
        const newConditionsData = conditionsData.filter((item) => idsToKeep.includes(item.type));

        if (waypointIdDeleted.length > 0) {
          newConditionsValues.forEach((item: IChipInputItem) => {
            if (waypointIdDeleted.includes(String(item.id))) {
              waypointIdDeletedTemp.push(String(item.id));
            }
          });
        }

        setWaypointIdDeleted(waypointIdDeletedTemp);

        setConditionsData(newConditionsData);
        setValue('conditions', newConditionsValues);

        setConditionValues(newConditionsValues);
      }
    },
    [conditionValues]
  );

  const handleTimeFrameModalResponse = React.useCallback(
    (values: IChipInputItem, valuesToSend: TimeFrameDataType[]) => {
      if (timeframeData.length === 0) {
        setTimeframeData([valuesToSend]);
        return true;
      }

      let recordIdExistsInGroups = false;
      const newTimeFrameGroups = timeframeData.map((timeFrameGroup: TimeFrameDataType[]) => {
        return timeFrameGroup.map((item: TimeFrameDataType, index: number) => {
          if (
            valuesToSend[index] &&
            String(item.recordId) === String(valuesToSend[index].recordId) &&
            String(values.id) === String(item.recordId)
          ) {
            recordIdExistsInGroups = true;
            return valuesToSend[index];
          }
          return item;
        });
      });

      if (!recordIdExistsInGroups) {
        newTimeFrameGroups.push(valuesToSend);
      }

      setTimeframeData(newTimeFrameGroups);
      return true;
    },
    [timeframeData]
  );

  const handleRuleModalResponse = React.useCallback(
    (
      values: IChipInputItem,
      valuesToSent: { type: keyof IRuleFormValues; value: RuleFormValue }
    ) => {
      setConditionValues([...conditionValues, values]);

      const data: IRuleDataToSent[] = JSON.parse(JSON.stringify(conditionsData));

      const normalizeValue = (value: RuleFormValue): IRuleDataToSent['value'] => {
        if (typeof value === 'number') {
          return String(value);
        }
        if (Array.isArray(value)) {
          return value.map(String);
        }
        if (typeof value === 'object' && 'min' in value && 'max' in value) {
          return {
            min: value.min !== undefined ? String(value.min) : '',
            max: value.max !== undefined ? String(value.max) : '',
          };
        }
        return value as string | string[];
      };

      const existingItem = data.find((item) => item.type === values.id);

      if (existingItem) {
        existingItem.value = normalizeValue(valuesToSent.value);
      } else {
        data.push({
          type: valuesToSent.type,
          value: normalizeValue(valuesToSent.value),
        });
      }

      setConditionsData(data);

      const chipInputItems: IChipInputItem[] = data.map(({ type, value }) => ({
        id: type,
        label: type,
        value,
      }));

      setValue('conditions', chipInputItems);
      trigger('conditions');
    },
    [conditionValues, conditionsData]
  );

  const handleEditRuleModalResponse = React.useCallback(
    (
      values: IChipInputItem,
      valuesToSent: { type: keyof IRuleFormValues; value: RuleFormValue }
    ) => {
      const prevRule: IChipInputItem[] = conditionValues;
      const normalizedValue: IRuleDataToSent['value'] =
        typeof valuesToSent.value === 'object' && valuesToSent.value !== null
          ? Array.isArray(valuesToSent.value)
            ? valuesToSent.value.map(String)
            : {
                min: valuesToSent.value.min?.toString() ?? '',
                max: valuesToSent.value.max?.toString() ?? '',
              }
          : valuesToSent.value.toString();

      const ruleToSave: IRuleDataToSent = {
        type: valuesToSent.type,
        value: normalizedValue,
      };

      if (conditionValues.length > 0) {
        setEditDataConditions(ruleToSave); //
        if (prevRule.find((item: IChipInputItem) => item.id === editDataConditions?.type)) {
          prevRule.forEach((item: IChipInputItem) => {
            if (item.id === editDataConditions?.type) {
              item.label = values.label;
            }
          });

          setConditionValues(prevRule);
          setValue('conditions', prevRule);
        } else {
          setConditionValues([...conditionValues, values]);
          setValue('conditions', [...conditionValues, values]);
          trigger('conditions');
        }
      } else {
        setConditionValues([...conditionValues, values]);
        setValue('conditions', [...conditionValues, values]);
        trigger('conditions');
      }

      const data: IRuleDataToSent[] = JSON.parse(JSON.stringify(conditionsData));

      const foundItem = data.find((item: IRuleDataToSent) => item.type === values.id);
      if (foundItem) {
        foundItem.value = normalizedValue;
      } else {
        data.push(ruleToSave);
      }

      setConditionsData(data);
      // setValue('conditions', data);
    },
    [conditionValues, conditionsData, editDataConditions?.type]
  );

  const handleRemoveRecipient = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      event.preventDefault();
      trackEvent(['alarm', 'click', 'Alarm form remove recipient']);
      const newRecipients: IRecipient[] = [];
      const id = event.currentTarget.getAttribute('data-key');
      if (recipientValues) {
        recipientValues.forEach((item, key: number) => {
          if (String(id) !== String(key)) {
            newRecipients.push(item);
          }
        });
      }
      setRecipientValues(newRecipients);
    },
    [recipientValues]
  );

  const handleEditRecipient = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (event) {
        event.preventDefault();
      }
      const id = event.currentTarget.getAttribute('data-key');
      trackEvent(['alarm', 'click', 'Alarm form open edit recipient modal']);
      if (recipientValues) {
        recipientValues.forEach((item, key: number) => {
          if (String(id) === String(key)) {
            item.key = String(key);
            if (!item.fullname) {
              item.fullname = item.name;
            }
            setEditingRecipient(item);
          }
        });
      }
      setIsRecipientModalOpen(!isRecipientModalOpen);
    },
    [isRecipientModalOpen, recipientValues]
  );

  const handleChangeRecipientTableValues = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const id = event.currentTarget.getAttribute('data-id');
      const isChecked = event.target.checked;
      const key = event.currentTarget.getAttribute('data-key');
      const newRecipients = JSON.parse(JSON.stringify(recipientValues));
      trackEvent(['alarm', 'click', 'Alarm form change recipient table values']);
      if (key) {
        const newRecipient = newRecipients[key];

        if (String(id) === 'map') {
          newRecipient.viaMap = !!isChecked;
        } else if (String(id) === 'ride') {
          newRecipient.viaRide = !!isChecked;
        } else if (String(id) === 'email') {
          newRecipient.viaEmail = !!isChecked;
        } else if (String(id) === 'phone') {
          newRecipient.viaCall = !!isChecked;
        } else if (String(id) === 'push') {
          newRecipient.viaPush = !!isChecked;
        }

        if (isChecked) {
          switch (id) {
            case 'email':
              newRecipient.viaEmail = !!newRecipient.email;
              if (!newRecipient.viaEmailPeriodicity && newRecipient.email) {
                newRecipient.viaEmailPeriodicity = 'once_per_day';
              }
              break;
            case 'phone':
              newRecipient.viaCall = !!(newRecipient.phone || newRecipient.telephone);
              if (
                !newRecipient.viaCallPeriodicity &&
                (newRecipient.phone || newRecipient.telephone)
              ) {
                newRecipient.viaCallPeriodicity = 'once_per_day';
              }
              break;
            case 'push':
              newRecipient.viaPush = !!(newRecipient.phone || newRecipient.telephone);
              if (
                !newRecipient.viaPushPeriodicity &&
                (newRecipient.phone || newRecipient.telephone)
              ) {
                newRecipient.viaPushPeriodicity = 'once_per_day';
              }
              break;
            default:
          }
        }

        newRecipients[key] = newRecipient;
        setRecipientValues(newRecipients);
      }
    },
    [recipientValues]
  );

  React.useEffect(() => {
    if (backendValidationErrors && Object.keys(backendValidationErrors).length === 0) {
      setFieldWarnings({});
      return;
    }

    if (backendValidationErrors) {
      const newFieldWarnings: IFieldWarning = {};

      Object.entries(backendValidationErrors).forEach(([fieldName, messages]) => {
        newFieldWarnings[fieldName] = messages.join('<br />');
      });

      setFieldWarnings(newFieldWarnings);
    }
  }, [backendValidationErrors]);

  const buttons: CmdSubpageButton[] = [
    {
      id: 'alarm-close-button',
      title: f({ id: 'form.close' }),
      type: 'button',
      className: '',
      closeCallback: props.closeCallback,
    },
    {
      id: 'alarm-submit-button',
      title: f({ id: 'form.save' }),
      type: 'button',
      className: '',
      submitCallback: handleSubmit(handleOnSubmit),
    },
  ];

  const getIsSavingAllowed = () => {
    if (props.id && PermissionsService.hasAccess(PERMISSION_ALARM, ['update'], selectedCustomers)) {
      return true;
    }
    if (
      !props.id &&
      PermissionsService.hasAccess(PERMISSION_ALARM, ['create'], selectedCustomers)
    ) {
      return true;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['alarmFormData', props.id],
        queryFn: () => getAlarmData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!props.id,
      },
      {
        queryKey: ['alarmFormValidation'],
        queryFn: () => getAlarmValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !props.id,
      },
      {
        queryKey: ['alarmFormRecipientValues'],
        queryFn: () => getRecipientValues(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !props.id,
      },
      {
        queryKey: [
          'alarmFormGroupSelectectVehicles',
          ridePrivacyValue,
          conditionsData,
          rideOwnershipValue,
          minPending,
        ],
        queryFn: () => getGroupSelectVehicles(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: Number(minPending) > 0 || Number(minPending) === 0,
      },
      {
        queryKey: [
          'alarmFormGroupSelectVehiclesGroups',
          ridePrivacyValue,
          conditionsData,
          rideOwnershipValue,
          minPending,
        ],
        queryFn: () => getGroupSelectVehiclesGroups(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: Number(minPending) > 0 || Number(minPending) === 0,
      },
      {
        queryKey: [
          'alarmFormBusinessValidation',
          ridePrivacyValue,
          conditionsData,
          rideOwnershipValue,
          minPending,
          // notifMessageValue,
          editingData,
        ],
        queryFn: () => getBusinessValidation(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: Number(minPending) > 0 || Number(minPending) === 0,
      },
    ],
  });
  const { data: _alarmFormDataEdit, isLoading: isLoadingAlarmFormDataEdit } = results[0];
  const { data: _alarmFormValidation, isLoading: isLoadingAlarmFormValidation } = results[1];
  const { data: _alarmFormRecipientValues, isLoading: isLoadingAlarmRecipientValues } = results[2];
  const { data: _groupSelectectVehiclesData, isLoading: isLoadingGroupSelectectVehicles } =
    results[3];
  const { data: _groupSelectVehiclesGroupsData, isLoading: isLoadingGroupSelectVehiclesGroups } =
    results[4];
  const { data: _businessValidation, isLoading: isLoadingBusinessValidation } = results[5];

  const isLoadingData =
    isLoadingAlarmFormDataEdit ||
    isLoadingAlarmFormValidation ||
    isLoadingAlarmRecipientValues ||
    isLoadingGroupSelectectVehicles ||
    isLoadingGroupSelectVehiclesGroups ||
    isLoadingBusinessValidation;

  const mappedGroupSelectVehiclesGroups = Object.fromEntries(
    Object.entries(groupSelectVehiclesGroups).map(([key, items]) => [
      key,
      items.map((item) => ({
        ...item,
        vehicles: String(item.vehicles),
        total: String(item.total),
      })),
    ])
  );

  return (
    <CmdSubpage
      id="alarm-formik-form"
      title={f({ id: 'alarms.title' })}
      subtitle={props.id ? editingData?.name : f({ id: 'alarms.subpage.addAlarm' })}
      onlineHelp={<OnlineHelp topic={props.id ? 'edit-alarms' : 'add-alarm'} />}
      buttons={
        getIsSavingAllowed() ? buttons : buttons.filter((item) => item.id === 'alarm-close-button')
      }
      loader={isLoadingData || loader}
    >
      {validation === null ? null : (
        <div>
          {isAlertShown && (
            <CmdAlert
              data={{ title: f({ id: 'alarms.alert.title' }), type: 'info' }}
              isAbsolute={true}
              onCloseCallback={handleCloseAlert}
              id="vehicle-group-alert"
            >
              <div>
                {`${f({ id: 'alarms.alert.message' })} `}
                <a href="" onClick={handleVehicleGroupsModal} data-cy="create-vehicle-group">
                  {f({ id: 'alarms.alert.button' })}
                </a>
              </div>
            </CmdAlert>
          )}
          {/* {isDeactivateWarningShown && (
                  <Alert
                    id="vehicle-alert"
                    data={{
                      title: 'alarms.modal.vehciles.errors.title',
                      message: 'alarms.modal.vehciles.errors.message',
                      type: 'warning',
                    }}
                    isAbsolute={true}
                    onCloseCallback={handleCloseDeactivateWarning}
                  />
                )} */}
          {/* {isAlarmWarning && (
                  <Alert
                    id="rule-alert"
                    data={{
                      title: 'alarms.alert.bad.rule.title',
                      message: `${
                        Number(formikRenderProps.values.minPending) > 0
                          ? 'alarms.alert.bad.rule.waypoint.message'
                          : 'alarms.alert.bad.rule.message'
                      }`,
                      type: `${
                        Number(formikRenderProps.values.minPending) > 0 ? 'error' : 'warning'
                      }`,
                    }}
                    isAbsolute={true}
                    onCloseCallback={handleCloseWarning}
                  />
                )} */}
          <div className="row">
            <div className="col-xxl-6 boxes-equal-height">
              <CmdBox title={f({ id: 'alarms.section.basicInformation' })} id="basic-information">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      id="name"
                      type="text"
                      label={f({ id: 'alarms.section.basicInformation.name' })}
                      required={true}
                      tooltip={f({ id: 'alarms.section.basicInformation.name.tooltip' })}
                      warningMessage={fieldWarnings['name'] || undefined}
                      onChangeCallback={(value) => {
                        setValue('name', value);
                        trigger('name');
                      }}
                      error={errors?.name?.message}
                      touched={!!errors?.name?.message}
                    />
                  )}
                />
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <CmdSwitch
                      {...field}
                      id="enabled"
                      label={f({ id: 'alarms.section.basicInformation.enabled' })}
                      disabled={isDisabledSwitch}
                      tooltip={f({ id: 'alarms.section.basicInformation.enabled.tooltip' })}
                      warningMessage={fieldWarnings['enabled'] || undefined}
                      setValue={(value: TEnable) => {
                        setValue('enabled', value);
                        trigger('enabled');
                      }}
                    />
                  )}
                />
                <Controller
                  name="validFrom"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      id="validFrom"
                      mode="single"
                      label={f({ id: 'alarms.section.basicInformation.validFrom' })}
                      tooltip={f({ id: 'alarms.section.basicInformation.validFrom.tooltip' })}
                      warningMessage={fieldWarnings['validFrom'] || undefined}
                      dateFormat="dd.MM.yyyy"
                      onChangeCallback={(value) => {
                        if (value instanceof Date) {
                          setValue('validFrom', value);
                        }
                        trigger('validFrom');
                        trackEvent(['alarm', 'change', 'Alarm form valid from']);
                      }}
                      error={errors.validFrom?.message as string}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="validTo"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      {...field}
                      id="validTo"
                      label={f({ id: 'alarms.section.basicInformation.validTo' })}
                      tooltip={f({ id: 'alarms.section.basicInformation.validTo.tooltip' })}
                      warningMessage={fieldWarnings['validTo'] || undefined}
                      dateFormat="dd.MM.yyyy"
                      onChangeCallback={(value) => {
                        if (value instanceof Date) {
                          // value is a Date
                          setValue('validTo', value);
                        }

                        // setValue('validTo', value as Date);
                        trigger('validTo');
                        trackEvent(['alarm', 'change', 'Alarm form valid to']);
                      }}
                      error={errors.validTo?.message as string}
                      touched={true}
                    />
                  )}
                />
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <CmdTextarea
                      {...field}
                      id="description"
                      label={f({ id: 'alarms.section.basicInformation.description' })}
                      rows={5}
                      tooltip={f({ id: 'alarms.section.basicInformation.description.tooltip' })}
                      warningMessage={fieldWarnings['description'] || undefined}
                      setValue={(value: string) => {
                        setValue('description', value);
                        trigger('description');
                      }}
                      error={errors.description?.message as string}
                      touched={true}
                    />
                  )}
                />
              </CmdBox>
            </div>
            <div className="col-xxl-6 boxes-equal-height">
              <CmdBox
                title={f({ id: 'alarms.section.timeFrame' })}
                buttons={timeFrameButtons}
                id="timeframes"
              >
                <Controller
                  name="timeFrames"
                  control={control}
                  render={({ field }) => (
                    <ChipInput
                      {...field}
                      id="timeFrames"
                      label={f({ id: 'alarms.section.timeFrame.timeFrames' })}
                      note={f({ id: 'alarms.section.timeFrame.timeFrames.note' })}
                      onClickItemCallback={handleEditTimeFrameModal}
                      onClickCallback={handleTimeFrameModal}
                      onClickRemoveItemCallback={handleRemoveTimeFrameItem}
                      tooltip={f({ id: 'alarms.section.timeFrame.timeFrames.tooltip' })}
                      labelTooltip={f({ id: 'alarms.section.timeFrame.timeFrames.tooltip' })}
                      warningMessage={fieldWarnings['timeFrames'] || undefined}
                      control={control}
                    />
                  )}
                />
              </CmdBox>
              <CmdBox
                title={f({ id: 'alarms.section.rule' })}
                buttons={ruleSectionButtons}
                id="conditions"
              >
                <Controller
                  name="rideOwnership"
                  control={control}
                  render={({ field }) => (
                    <CmdRadioButton
                      {...field}
                      id="rideOwnership"
                      label={f({ id: 'alarms.section.vehicles.forvehicles' })}
                      options={[
                        { value: 'own', item: f({ id: 'alarms.section.vehicles.ofme' }) },
                        { value: 'any', item: f({ id: 'alarms.section.vehicles.all' }) },
                      ]}
                      required={true}
                      tooltip={f({ id: 'alarms.section.vehicles.forvehicles.tooltip' })}
                      warningMessage={fieldWarnings['rideOwnership'] || undefined}
                      setValue={(value: string) => {
                        setValue('rideOwnership', value);
                        trigger('rideOwnership');
                        setRideOwnershipValue(value);
                        trigger('vehicleId');
                        trackEvent(['alarm', 'change', 'Alarm form ride ownership']);
                      }}
                    />
                  )}
                />
                {watch('rideOwnership') === 'any' && (
                  <Controller
                    name="ridePrivacy"
                    control={control}
                    render={({ field }) => (
                      <CmdRadioButton
                        {...field}
                        id="ridePrivacy"
                        label={f({ id: 'alarms.section.vehicles.ridePrivacy' })}
                        options={[
                          { value: 'public', item: f({ id: 'alarms.section.vehicles.public' }) },
                          {
                            value: 'private',
                            item: f({ id: 'alarms.section.vehicles.private' }),
                          },
                          { value: 'any', item: f({ id: 'alarms.section.vehicles.all' }) },
                        ]}
                        required={true}
                        warningMessage={fieldWarnings['ridePrivacy'] || undefined}
                        setValue={(value: string) => {
                          setValue('ridePrivacy', value);
                          trigger('ridePrivacy');
                          setRidePrivacyValue(value);
                          trigger('vehicleId');
                          trackEvent(['alarm', 'change', 'Alarm form ride privacy']);
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  name="conditions"
                  control={control}
                  render={({ field }) => (
                    <ChipInput
                      {...field}
                      id="conditions"
                      label={f({ id: 'alarms.section.conditions.rule.title' })}
                      note={f({ id: 'alarms.section.conditions.rule.note' })}
                      required={true}
                      onClickItemCallback={handleEditRuleModal}
                      onClickCallback={handleRuleModal}
                      onClickRemoveItemCallback={handleRemoveConditionItem}
                      tooltip={f({ id: 'alarms.section.conditions.rule.tooltip' })}
                      labelTooltip={
                        isDisabledTooltip
                          ? undefined
                          : f({ id: 'alarms.section.conditions.rule.tooltip' })
                      }
                      warningMessage={fieldWarnings.conditions || undefined}
                      control={control}
                    />
                  )}
                />
                <Controller
                  name="minPending"
                  control={control}
                  render={({ field }) => (
                    <CmdInput
                      {...field}
                      type="number"
                      id="minPending"
                      label={f({ id: 'alarms.section.timeFrame.minPending' })}
                      placeholder={f({ id: 'alarms.section.timeFrame.minPending.placeholder' })}
                      required={true}
                      tooltip={f({ id: 'alarms.section.timeFrame.minPending.toolip' })}
                      step={1}
                      warningMessage={fieldWarnings['minPending'] || undefined}
                      // onBlurCallBack={handleOnBlurMinPending}
                      onChangeCallback={(value) => {
                        setValue('minPending', value);
                        trigger('minPending');
                      }}
                    />
                  )}
                />
              </CmdBox>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <CmdBox title={f({ id: 'alarms.section.vehicles' })} id="vehicles">
                <Controller
                  name="vehicleId"
                  control={control}
                  render={({ field }) => (
                    <CmdGroupChipSelect
                      {...field}
                      id="vehicleId"
                      label={f({ id: 'alarms.section.vehicles.vehicleId' })}
                      placeholder={f({ id: 'form.choose' })}
                      errorTooltip={f({ id: 'alarms.modal.vehciles.error_items' })}
                      allItemsTooltip={f({ id: 'alarms.modal.vehciles.all_items' })}
                      searchPlaceholder={f({ id: 'form.search' })}
                      selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                      resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                      options={groupSelectVehicles}
                      onChangeCallback={showVehicleInfoAlert}
                      showDetail={handleShowVehicleList}
                      required={true}
                      itemsNumber={6}
                      tooltip={f({ id: 'alarms.section.vehicles.vehicleId.tooltip' })}
                      searchTooltip={f({
                        id: 'alarms.section.vehicles.vehicleId.search.tooltip',
                      })}
                      selectButtonTooltip={f({
                        id: 'alarms.section.vehicles.vehicleId.selectButton.tooltip',
                      })}
                      resetButtonTooltip={f({
                        id: 'alarms.section.vehicles.vehicleId.resetButton.tooltip',
                      })}
                      warningMessage={fieldWarnings['vehicleId'] || undefined}
                      error={
                        watch('vehicleGroupId').length === 0
                          ? (errors.vehicleId?.message as string)
                          : undefined
                      }
                      touched={true}
                      setValue={(value: string[]) => {
                        setValue('vehicleId', value);
                        trigger('vehicleId');
                      }}
                    />
                  )}
                />
                <Controller
                  name="vehicleGroupId"
                  control={control}
                  render={({ field }) => (
                    <GroupChipSelectUniqueHookForm
                      {...field}
                      id="vehicleGroupId"
                      label={f({ id: 'alarms.section.vehicles.vehicleGroupId' })}
                      placeholder={f({ id: 'form.choose' })}
                      searchPlaceholder={f({ id: 'form.search' })}
                      selectButton={f({ id: 'form.groupChipSelectUnique.button.apply' })}
                      resetButton={f({ id: 'form.groupChipSelectUnique.button.reset' })}
                      options={mappedGroupSelectVehiclesGroups}
                      showDetail={handleShowVehicleGroupList}
                      showPartialDetail={handleShowVehicleListInGroup}
                      itemsNumber={6}
                      searchTooltip={f({
                        id: 'alarms.section.vehicles.formField.vehicleGroupId.search.tooltip',
                      })}
                      selectButtonTooltip={f({
                        id: 'alarm.section.vehicles.formField.vehicleGroupId.apply.tooltip',
                      })}
                      resetButtonTooltip={f({
                        id: 'alarm.section.vehicles.formField.vehicleGroupId.reset.tooltip',
                      })}
                      tooltip={f({ id: 'alarms.section.vehicles.vehicleGroupId.tooltip' })}
                      warningMessage={fieldWarnings['vehicleGroupId'] || undefined}
                      control={control}
                      onChangeCallback={(_number: number, value) => {
                        setValue('vehicleGroupId', value as string[]);
                        trigger('vehicleGroupId');
                        trigger('vehicleId');
                        trackEvent(['alarm', 'change', 'Alarm form vehicle group']);
                      }}
                    />
                  )}
                />
              </CmdBox>
              <CmdBox
                title={f({ id: 'alarms.section.recipient' })}
                headerButtons={recipientSectionButtons}
                id="recipient"
              >
                {recipientValues && recipientValues.length === 0 && (
                  <div style={{ color: '#ba0e48', marginBottom: '35px' }}>
                    {f({ id: 'alarms.alert.bad.recipient.message' })}
                  </div>
                )}
                {recipientValues && recipientValues.length > 0 && (
                  <div className="form-group">
                    <div className="table-responsive height-auto">
                      <table className="table table-striped table-bordered table-listings m-0">
                        <thead>
                          <tr>
                            <th>{f({ id: 'alarms.section.recipient' })}</th>
                            <th>{f({ id: 'alarms.section.recipient.email' })}</th>
                            <th>{f({ id: 'alarms.section.recipient.cellphone' })}</th>
                            <th className="text-center">
                              {f({ id: 'alarms.section.recipient.map' })}
                            </th>
                            <th className="text-center">
                              {f({ id: 'alarms.section.recipient.ride' })}
                            </th>
                            <th className="text-center">
                              {f({ id: 'alarms.section.recipient.email' })}
                            </th>
                            <th className="text-center">
                              {f({ id: 'alarms.section.recipient.ring' })}
                            </th>
                            <th className="text-center">
                              {f({ id: 'alarms.section.recipient.push' })}
                            </th>
                            <th className="text-center">{f({ id: 'waypoints.edit' })}</th>
                            <th className="text-center">{f({ id: 'table.deleteBtn' })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.values(recipientValues).map((item, key: number) => {
                            return (
                              <tr
                                key={`table-tr-${key}`}
                                data-cy={`alarm-subpage-table-row-${key}`}
                              >
                                <td>{item.fullname || item.name || ''}</td>
                                <td>{item.email}</td>
                                <td>{item.telephone || item.phone}</td>
                                <td className="text-center">
                                  <div className="e-checkbox">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={!!item.viaMap && !!item.userId}
                                        onChange={handleChangeRecipientTableValues}
                                        disabled={!item.userId}
                                        data-id="map"
                                        data-key={key}
                                        data-cy="map"
                                        aria-label="map"
                                      />
                                      <span>&nbsp;</span>
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="e-checkbox">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={!!item.viaRide && !!item.userId}
                                        onChange={handleChangeRecipientTableValues}
                                        disabled={!item.userId}
                                        data-id="ride"
                                        data-key={key}
                                        data-cy="ride"
                                        aria-label="ride"
                                      />
                                      <span>&nbsp;</span>
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="e-checkbox">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={!!item.viaEmail && !!item.email}
                                        onChange={handleChangeRecipientTableValues}
                                        disabled={!item.email}
                                        data-id="email"
                                        data-key={key}
                                        data-cy="email"
                                        aria-label="email"
                                      />
                                      <span>&nbsp;</span>
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="e-checkbox">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={
                                          !!item.viaCall && (!!item.telephone || !!item.phone)
                                        }
                                        onChange={handleChangeRecipientTableValues}
                                        data-id="phone"
                                        data-key={key}
                                        disabled={!(item.telephone || item.phone)}
                                        data-cy="phone"
                                        aria-label="phone"
                                      />
                                      <span>&nbsp;</span>
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <div className="e-checkbox">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={
                                          !!item.viaPush && (!!item.telephone || !!item.phone)
                                        }
                                        onChange={handleChangeRecipientTableValues}
                                        data-id="push"
                                        data-key={key}
                                        disabled={!(item.telephone || item.phone)}
                                        data-cy="push"
                                        aria-label="push"
                                      />
                                      <span>&nbsp;</span>
                                    </label>
                                  </div>
                                </td>
                                <td className="text-center">
                                  <a
                                    className="cmd-icon cmd-icon-edit text-gray border-0 background-none"
                                    onClick={handleEditRecipient}
                                    data-key={key}
                                    style={{ padding: '14px 45px', margin: '-14px -45px' }}
                                    data-cy="alarm-subpage-table-edit"
                                  >
                                    {/* No content */}
                                  </a>
                                </td>
                                <td className="text-center">
                                  <a
                                    className="cmd-icon cmd-icon-bin text-gray border-0 background-none"
                                    onClick={handleRemoveRecipient}
                                    data-key={key}
                                    style={{ padding: '14px 45px', margin: '-14px -45px' }}
                                    data-cy="alarm-subpage-table-remove"
                                  >
                                    {/* No content */}
                                  </a>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <ContentEditableTextarea2
                  // id="notificationMessage"
                  name="notificationMessage"
                  label={f({ id: 'alarms.section.recipients.title' })}
                  // note={f({ id: 'alarms.section.recipients.note' })}
                  // options={notifMessageItems}
                  // rows={10}
                  // tooltip={f({ id: 'alarms.section.recipients.notificationMessage.tooltip' })}
                  value={
                    editingData && Object.keys(editingData).length > 0
                      ? editingData.notificationMessage
                      : ''
                  }
                  error={notificationMessageError}
                  required={isNotificationMessageRequired}
                  onChangeCallback={handleNotifMessageValue}
                  gdprValidation={notifMessageWarningValidation}
                  warningMessage={fieldWarnings['notificationMessage'] || undefined}
                  handleBusinessValidation={getBusinessValidation}
                />
              </CmdBox>
            </div>
          </div>
          {isRuleModalOpen && (
            <RuleForm
              rideOwnerShipValue={watch('rideOwnership')}
              ridePublicityValue={watch('ridePrivacy')}
              selectedChips={watch('conditions') as ISelectedChips[]}
              closeCallback={handleCloseRuleModal}
              responseCallback={handleRuleModalResponse}
              openCallback={handleResetRuleModal}
            />
          )}
          {isEditRuleModalOpen && (
            <EditRuleForm
              rideOwnerShipValue={watch('rideOwnership')}
              ridePublicityValue={watch('ridePrivacy')}
              selectedChips={watch('conditions') as ISelectedEditChips[]}
              closeCallback={handleCloseEditRuleModal}
              responseCallback={handleEditRuleModalResponse}
              dataForEdit={editDataConditions}
            />
          )}
          {isRecipientModalOpen && (
            <RecipientForm
              closeCallback={handleRecipientModal}
              responseCallback={handleRecipientModalResponse}
              editingRecipient={editingRecipient}
              editCallback={handleRecipientModalEdit}
              putRecipients={recipientValues || []}
            />
          )}
          {isVehicleGroupsModal && (
            <VehicleGroupsForm
              closeCallback={handleVehicleGroupsModal}
              responseCallback={handleVehicleGroupsData}
            />
          )}
          {isVehicleListOpen && (
            <VehicleListForm
              selectedItems={watch('vehicleId')}
              options={groupSelectVehicles}
              closeCallback={handleShowVehicleList}
              responseCallback={(_type, value) => {
                setValue('vehicleId', value);
                trigger('vehicleId');
              }}
            />
          )}
          {isVehicleGroupListOpen && (
            <VehicleGroupListForm
              selectedItems={watch('vehicleGroupId')}
              options={groupSelectVehiclesGroups}
              closeCallback={handleShowVehicleGroupList}
            />
          )}
          {clickedVehicleGroupId && (
            <VehicleListInGroupForm
              groupData={newVehicleGroupsArray}
              closeCallback={handleCloseClickedVehicleGroup}
              groupId={clickedVehicleGroupId}
              rideOwnership={watch('rideOwnership')}
              conditions={watch('conditions')}
            />
          )}
          {isTimeFrameModalOpen && (
            <TimeFrameForm
              closeCallback={handleTimeFrameModal}
              openCallback={handleResetTimeFrameModal}
              responseCallback={handleTimeFrameModalResponse}
              id={editTimeFrameId}
              data={timeframeData}
            />
          )}
        </div>
      )}
    </CmdSubpage>
  );
}
